import { hiringLinksAPI, type HiringTimelineEvent } from '../../services/api';

/**
 * A link's WHOLE timeline, for its card.
 *
 * The API answers a timeline one page at a time, oldest first, and says in `nextAfter`
 * where the next page starts (null on the last page). One page is not the timeline: once
 * Recruitment's messages arrive a busy opening passes a page, and the line that says how a
 * link ended - which `closedHowOf` looks for - is always among the newest. So every page
 * is read, each asked for with the `nextAfter` of the one before, and the lines are kept
 * in the order they came: oldest first, exactly as one page was.
 *
 * The reading goes on to the LAST page (15 Oct 2026, the product owner: no hiring list stops
 * short of its end). It cannot run away: a page that does not move on - the same cursor again,
 * or a page that brings nothing and still names a next one - fails the reading.
 */

export interface TimelineRead {
  /** Every line that was read, oldest first. */
  events: HiringTimelineEvent[];
  /** Always false now that every page is read; kept so a card that says "not all" never has to. */
  cut: boolean;
}

/**
 * `stopped` is asked after every answer. Once it says yes (the card has closed, or its
 * timeline was asked for again) no further page is asked for, and the answer is null:
 * nothing of that reading is to be shown. A page that fails fails the whole reading, as
 * the one read always did.
 */
export const readTimeline = async (
  projectId: number,
  linkId: string,
  stopped: () => boolean,
): Promise<TimelineRead | null> => {
  const events: HiringTimelineEvent[] = [];
  let after: string | null = null;
  for (let page = 1; ; page += 1) {
    // The first page is asked for exactly as it always was: no cursor.
    const res = after === null
      ? await hiringLinksAPI.timeline(projectId, linkId)
      : await hiringLinksAPI.timeline(projectId, linkId, after);
    if (stopped()) return null;
    const answer = res?.data?.data;
    if (Array.isArray(answer?.events)) events.push(...answer.events);
    const next = typeof answer?.nextAfter === 'string' && answer.nextAfter ? answer.nextAfter : null;
    // No page is taken on trust (as `readAll` in useStoreHiring): a later page that cannot be read is not the end of the timeline, and the same cursor again - or a page that brings nothing and still names a next one - would be the same lines up to the bound.
    if ((page > 1 && !Array.isArray(answer?.events)) || (next !== null && (next === after || !answer?.events?.length))) throw new Error('hiring timeline did not move on');
    if (next === null) return { events, cut: false };
    after = next;
  }
};
